import { App as CapacitorApp } from '@capacitor/app'
import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { getIsOnline } from '../helpers/networkStatus'
import { useNetwork } from './useNetwork'

type PagePollingOptions = {
  /** Network refresh to run on every trigger. */
  refresh: () => unknown
  /** Route that counts as "on this page" (the tab shell never unmounts pages). */
  pagePath: string
  /** Poll cadence while the app is visible and online. */
  intervalMs: number
}

/**
 * Keeps an always-mounted page fresh: interval poll, page re-enter, native
 * resume and reconnect. Every trigger no-ops while offline (or hidden), so
 * offline mode keeps serving the cache untouched.
 */
export const usePagePolling = ({
  refresh,
  pagePath,
  intervalMs,
}: PagePollingOptions) => {
  const { isOnline } = useNetwork()

  // Reconnect — the list should reflect server-side changes (and the synced
  // results of queued offline actions) without waiting for a manual refresh
  const wasOnline = useRef(isOnline)
  useEffect(() => {
    if (isOnline && !wasOnline.current) refresh()
    wasOnline.current = isOnline
  }, [isOnline, refresh])

  // Poll — no-ops while hidden or offline
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState !== 'visible') return
      if (!getIsOnline()) return
      refresh()
    }, intervalMs)
    return () => clearInterval(id)
  }, [intervalMs, refresh])

  // Re-entering the page (never unmounts, so route change = view enter)
  const { pathname } = useLocation()
  const wasOnPage = useRef(pathname === pagePath)
  useEffect(() => {
    const onPage = pathname === pagePath
    const entered = onPage && !wasOnPage.current
    if (entered && getIsOnline()) refresh()
    wasOnPage.current = onPage
  }, [pathname, pagePath, refresh])

  // Native resume — the page is stalest after backgrounding
  useEffect(() => {
    const subscription = CapacitorApp.addListener('resume', () => {
      if (getIsOnline()) refresh()
    })
    return () => {
      void subscription.then((sub) => sub.remove())
    }
  }, [refresh])
}
